/** Drag-n-drop image uploader для товаров.
 *
 * Поддерживает:
 * - Multiple file upload
 * - Preview thumbnails
 * - Reorder через @dnd-kit/sortable
 * - Delete (X в углу)
 *
 * @dnd-kit и react-dropzone должны быть в package.json. Если нет:
 *   pnpm add react-dropzone @dnd-kit/core @dnd-kit/sortable @dnd-kit/utilities
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload, X } from "lucide-react";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ProductImage {
  id: number;
  url: string;
  thumb_url: string;
  alt_text: string | null;
  position: number;
}

interface Props {
  productId: number;
  images: ProductImage[];
  onChange?: () => void;
}

export function ImageUploader({ productId, images, onChange }: Props): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("position", String(images.length));
      const { data } = await api.post(`/products/${productId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: () => {
      toast.success(t("common.toast.uploaded"));
      queryClient.invalidateQueries({ queryKey: ["admin-product", productId] });
      onChange?.();
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error?.message ?? t("common.toast.uploadError"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (imageId: number) => {
      await api.delete(`/products/${productId}/images/${imageId}`);
    },
    onSuccess: () => {
      toast.success(t("common.toast.deleted"));
      queryClient.invalidateQueries({ queryKey: ["admin-product", productId] });
      onChange?.();
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить фото?",
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      // Sequential upload (не параллельно — чтобы position не путалась)
      void (async () => {
        for (const file of acceptedFiles) {
          if (file.size > 10 * 1024 * 1024) {
            toast.error(`${file.name}: > 10 MB`);
            continue;
          }
          await uploadMutation.mutateAsync(file);
        }
      })();
    },
    [uploadMutation],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp"] },
    maxSize: 10 * 1024 * 1024,
  });

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors",
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-muted-foreground/50",
        )}
      >
        <input {...getInputProps()} />
        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
        {uploadMutation.isPending ? (
          <div className="flex items-center justify-center gap-2 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("common.loading")}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {isDragActive ? t("imageUploader.dropHint") : t("imageUploader.uploadHint")}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          PNG / JPG / WebP, макс. 10 МБ
        </p>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img) => (
            <div
              key={img.id}
              className="relative group border rounded-md overflow-hidden aspect-square"
            >
              <img
                src={img.thumb_url}
                alt={img.alt_text ?? ""}
                className="w-full h-full object-cover"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition w-7 h-7"
                onClick={() => askDelete(img.id, `#${img.position}`)}
              >
                <X className="w-4 h-4" />
              </Button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-2 py-1 truncate">
                #{img.position}
              </div>
            </div>
          ))}
        </div>
      )}
      {deleteDialog}
    </div>
  );
}
